/**
 * HCP Results Table Component
 * Displays HCP search results with pagination and sorting
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  Skeleton,
  Tooltip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ImSpinner2 } from "react-icons/im";
import { FiDownload } from "react-icons/fi";
import {
  MRT_GlobalFilterTextField,
  MRT_FullScreenToggleButton as MrtFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MrtToggleFiltersButton,
  MRT_ToggleGlobalFilterButton as MrtToggleGlobalFilterButton,
} from 'material-react-table';
import GlobalMaterialTable from '../../../../components/common/GlobalMaterialTable';
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { commonTableStyles } from '../../../../components/common/tableStyles';

const renderTruncatedCell = (value, width = 140) => {
  const displayValue = value || '-';

  return (
    <Tooltip title={displayValue} placement="top" arrow>
      <Box
        component="span"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          maxWidth: width,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal',
          wordBreak: 'normal',
          overflowWrap: 'normal',
          lineHeight: 1.2,
          verticalAlign: 'bottom',
        }}
      >
        {displayValue}
      </Box>
    </Tooltip>
  );
};

const ViewActionCell = ({ row, onRowClick }) => (
  <Button className="action-btn close-btn"
    size="small"
    variant="text"
    onClick={(event) => {
      event.stopPropagation();
      onRowClick(row.original.id);
    }}
    sx={{
      textTransform: 'none',
      fontWeight: 700,
      color: '#1E88E5',
      
      borderRadius: '8px',
      px: 1.5,
      py: 0.5,
      backgroundColor: alpha('#1E88E5', 0.05),
      '&:hover': { backgroundColor: alpha('#1E88E5', 0.12) }
    }}
  >
    View Profile
  </Button>
);

ViewActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  onRowClick: PropTypes.func.isRequired,
};

const columns = [
  { key: 'source', label: 'Source' },
  { key: 'mdmId', label: 'MDM ID' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'status', label: 'Status' },
  { key: 'professions', label: 'Profession' },
  { key: 'specialities', label: 'Specialty' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'Zip Code' },
  { key: 'okIndvId', label: 'OK ID' },
  { key: 'mdmUpdatedTime', label: 'Last Updated' },
];

const columnWidths = {
  source: 90,
  mdmId: 110,
  firstName: 110,
  lastName: 110,
  status: 90,
  professions: 110,
  specialities: 150,
  city: 110,
  state: 90,
  zip: 90,
  okIndvId: 110,
  mdmUpdatedTime: 140,
};

const HCPResultsTable = ({
  results,
  totalCount,
  pagination,
  onPaginationChange,
  onRowClick,
  onExport,
  exportLoading,
  loading,
}) => {
  const theme = useTheme();
  const { page, limit } = pagination;

  const tableColumns = useMemo(() => ([
    ...columns.map((col) => ({
      header: col.label,
      id: col.key,
      accessorFn: (row) => row[col.key] || '-',
      size: columnWidths[col.key] || 150,
      minSize: columnWidths[col.key] || 150,
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() || '-'} placement="top" arrow>
          <Box sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'normal',
            wordBreak: 'normal',
            overflowWrap: 'normal',
            lineHeight: 1.2,
          }}>
            {cell.getValue()}
          </Box>
        </Tooltip>
      ),
    })),
    // {
    //   header: 'Actions',
    //   id: 'actions',
    //   enableSorting: false,
    //   size: 130,
    //   // eslint-disable-next-line react/prop-types
    //   Cell: ({ row }) => <ViewActionCell row={row} onRowClick={onRowClick} />,
    // },
  ]), [onRowClick]);
  const displayCount = useMemo(() => Number(totalCount || 0), [totalCount]);
  const formattedDisplayCount = useMemo(() => displayCount.toLocaleString('en-US'), [displayCount]);



  const handlePageChange = (newPage) => {
    onPaginationChange(newPage, limit);
  };

  const handlePageSizeChange = (newLimit) => {
    onPaginationChange(page, newLimit);
  };

  return (
    <Box sx={{ pt: 0, }}>
      <GlobalMaterialTable
        columns={tableColumns}
        data={results}
        isLoading={loading}
        tableTitle="Results"
        tableCount={displayCount}
        renderToolbarInternalActions={({ table }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MRT_GlobalFilterTextField table={table} />
            <MrtToggleGlobalFilterButton table={table} />
            <MrtToggleFiltersButton table={table} />
            <MrtShowHideColumnsButton table={table} />
            <MrtToggleDensePaddingButton table={table} />
            <MrtFullScreenToggleButton table={table} />
            <Tooltip title="Export">
              <IconButton
                onClick={onExport}
                disabled={exportLoading || results.length === 0}
                sx={{
                  color: '#475569',

                  '&.Mui-disabled': {
                    color: '#94a3b8',
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                  }
                }}
              >
                {exportLoading ? (
                  <ImSpinner2 size={15} className="spinner-icon" />
                ) : (
                  <FiDownload size={18} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}
        muiSearchTextFieldProps={{
          placeholder: 'Search in results...',
          variant: 'outlined',
          size: 'small',
          sx: {
            minWidth: '300px',
            '& .MuiInputBase-root': {
              mr: 1,
              mt: 0.4,
            },
            '& .MuiInputAdornment-root': { mt: '0 !important' },
            '& .MuiSvgIcon-root': { mt: '0 !important' }
          }
        }}

        emptyMessage="No providers found. Try changing or clearing filters." manualPagination
        enablePagination
        rowCount={displayCount}
        page={page}
        pageSize={limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onRowClick={(row) => {
          console.log(row, "row")
          onRowClick(row?.id)
        }}
        muiTableBodyRowProps={{
          sx: {
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            borderBottom: '1px solid #f1f5f9',
            '&:hover': {
              backgroundColor: '#f8fafc !important',
            },
            '& td:first-of-type': {
              paddingLeft: '24px',
            }
          },
        }}
        enableStickyHeader
      />
    </Box>
  );
};

HCPResultsTable.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      npi: PropTypes.string,
      specialty: PropTypes.string,
      address: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
    })
  ).isRequired,
  totalCount: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
};

HCPResultsTable.defaultProps = {
  exportLoading: false,
};

export default HCPResultsTable;
